import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ArrowRight, CheckCircle2, Play } from 'lucide-react';
import { featuresData } from '../../data/featuresData';
import TrainingSection from '../../components/custom/TrainingSection/TrainingSection';

export default function FeatureDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const content = id ? featuresData[id] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!content && id) {
      // Opcional: Redirecionar se não encontrar a funcionalidade
      // navigate('/funcionalidades');
    }
  }, [id, content]);

  if (!content) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-4xl font-black text-slate-900 mb-4">Funcionalidade não encontrada</h1>
        <p className="text-slate-500 mb-8">O recurso que você está procurando não existe ou foi movido.</p>
        <Link to="/funcionalidades" className="bg-[#690093] text-white px-8 py-3 rounded-full font-bold">
          Voltar para Funcionalidades
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      
      {/* ─── MINI NAVBAR ─────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-100 bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 py-4 flex items-center justify-between">
        <Link to="/funcionalidades" className="flex items-center gap-2 text-slate-500 hover:text-[#690093] transition-colors font-bold text-sm">
          <ChevronLeft size={18} /> Voltar para Funcionalidades
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{content.badge}</span>
          <Link to="/contato" className="bg-[#690093] text-white text-[11px] font-black uppercase tracking-widest px-6 py-2.5 rounded-full shadow-lg shadow-purple-200">
            Falar com Especialista
          </Link>
        </div>
      </nav>

      {/* ─── HERO SECTION ───────────────────────── */}
      <section className="pt-32 pb-24 lg:pt-48 lg:pb-40 relative">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
            <div className="w-full lg:w-1/2">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-block px-4 py-2 rounded-full bg-slate-100 text-[#690093] text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] mb-8"
              >
                {content.badge}
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-5xl lg:text-7xl xl:text-8xl font-black text-slate-900 leading-[0.95] tracking-tighter mb-8"
              >
                {content.title1} <br />
                <span style={{ textShadow: '0 10px 30px rgba(0,0,0,0.05)', color: 'transparent', WebkitTextStroke: '1px #64748b' }}>
                   {content.title2}
                </span>
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg lg:text-xl text-slate-500 mb-12 leading-relaxed max-w-xl"
              >
                {content.description}
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <button className="bg-[#690093] text-white px-10 py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-purple-200 hover:scale-105 transition-transform flex items-center gap-3">
                  Quero Implementar <ArrowRight size={16} />
                </button>
              </motion.div>
            </div>

            <div className="w-full lg:w-1/2 relative">
               <motion.div 
                 initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
                 animate={{ opacity: 1, scale: 1, rotate: 0 }}
                 transition={{ type: 'spring', damping: 15, delay: 0.4 }}
                 className="relative z-10 rounded-[3rem] overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.15)] border-8 border-white"
               >
                 <img src={content.heroImage} className="w-full aspect-4/5 lg:aspect-square object-cover" alt={content.title1} />
                 <div className="absolute inset-0 bg-linear-to-t from-black/40 to-transparent" />
               </motion.div>

               {/* Stats Floating Cards */}
               <div className="absolute -bottom-10 -left-10 z-20 hidden sm:block">
                  <motion.div 
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white p-8 rounded-4xl shadow-2xl border border-slate-50 grid grid-cols-1 gap-6 min-w-[200px]"
                  >
                    {content.stats.map((stat, i) => (
                      <div key={i}>
                        <p className="text-[10px] font-black uppercase tracking-widest text-[#690093] mb-1">{stat.label}</p>
                        <p className="text-3xl font-black text-slate-900 tracking-tighter">{stat.value}</p>
                      </div>
                    ))}
                  </motion.div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BENEFITS GRID ───────────────────────── */}
      <section className="py-24 lg:py-40 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-linear-to-r from-transparent via-slate-200 to-transparent" />
        
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mb-20">
            <h2 className="text-4xl lg:text-6xl font-black text-slate-900 leading-none tracking-tighter mb-6 uppercase">
              Vantagens que <br /> transformam.
            </h2>
            <p className="text-slate-500 text-lg">
              Cada detalhe foi projetado para maximizar a eficiência da sua operação e garantir mais lucro por cada litro vendido.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-10 rounded-[2.5rem] border border-slate-100 hover:border-[#690093]/20 hover:shadow-xl transition-all group"
                >
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-[#690093] mb-8 group-hover:scale-110 transition-transform duration-500">
                    <Icon size={28} />
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-4 tracking-tight uppercase leading-none">{benefit.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── TRAINING INTEGRATION ─────────────────── */}
      <div className="relative">
        <div className="absolute top-0 left-0 right-0 h-40 bg-linear-to-b from-slate-50 to-transparent pointer-events-none" />
        <TrainingSection 
          serviceType={content.serviceType}
          badge={`Suporte & Implementação: ${content.badge}`}
          title1="Implemente agora"
          title2={`o ${content.id.toUpperCase()}`}
          description={`Eleve o nível da sua operação com a funcionalidade de ${content.id}. Nossos especialistas garantem a configuração ideal para você extrair o máximo de lucro e eficiência.`}
        />
      </div>

      {/* ─── FINAL CTA ───────────────────────────── */}
      <section className="py-32 lg:py-48 px-6 text-center bg-[#0a0a0a] relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-linear-to-r from-transparent via-white/10 to-transparent" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px]" />
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-10"
        >
          <h2 className="text-5xl lg:text-8xl font-black text-white leading-none tracking-tighter mb-12 uppercase">
            Sua operação <br /> <span className="text-[#690093]">em outro nível.</span>
          </h2>
          <p className="text-white/40 text-lg mb-16 max-w-2xl mx-auto">
            Junte-se a centenas de postos que já utilizam a inteligência Lukos para automatizar a gestão e focar no que realmente importa: vender mais.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <button className="bg-white text-black px-12 py-6 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:scale-105 transition-transform flex items-center gap-3">
              Falar com consultor <Play size={16} fill="black" />
            </button>
            <button className="bg-black text-white border border-white/20 px-12 py-6 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-white/5 transition-all">
              Agendar Demo
            </button>
          </div>
        </motion.div>
      </section>

    </div>
  );
}
